import React from 'react'
import SessionItem from './session-item'
import abductionIllustration from 'theme/images/abduction-illustration.svg'
import PropTypes from 'prop-types'
import withFeedbackModal from 'theme/hoc/withFeedbackModal'
import withToggleFavouriteSession from 'theme/hoc/withToggleFavouriteSession'
import { getLocalDay, getLocalTime } from 'theme/utils/timezone-helpers'
import { useUserSessions } from 'theme/hooks/useUserSessions'
import { useTimezoneService } from 'theme/services/timezoneService'

const Sessions = ({
  sessionsList,
  toggleFavourite,
  conferenceLocation,
  conferenceTimezone,
  conferenceId,
  openFeedbackModal,
  post,
  format,
  showTimeinSession,
  conferenceVenueType,
}) => {
  const timezoneService = useTimezoneService()
  const { userUsedPlaces, registrations } = useUserSessions(conferenceId)
  const isInPerson = conferenceVenueType.indexOf('In-Person') !== -1
  const agendaDayTimezone = isInPerson ? conferenceTimezone : timezoneService.timezone

  // Group sessions by date and time
  const groupedSessions = React.useMemo(() => {
    const groups = {}
    let filteredList = sessionsList

    if (post) {
      filteredList = sessionsList.filter(
        (session) => session.data.Session_Groups?.[0]?.data.Recordings_Available === true,
      )
    }

    filteredList.forEach((session) => {
      const date = getLocalDay(session.data.UTC_Start_Time, agendaDayTimezone)
      const time = getLocalTime(session.data.UTC_Start_Time, agendaDayTimezone)
      const timeLocal = isInPerson
        ? getLocalTime(session.data.UTC_Start_Time, timezoneService.timezone)
        : time

      if (!groups[date]) {
        const dateObj = new Date(session.data.UTC_Start_Time)
        groups[date] = {
          dateParts: {
            weekday: dateObj.toLocaleString('en-GB', { weekday: 'long', timeZone: agendaDayTimezone }),
            day: dateObj.toLocaleString('en-GB', { day: 'numeric', timeZone: agendaDayTimezone }),
            month: dateObj.toLocaleString('en-GB', { month: 'long', timeZone: agendaDayTimezone }),
            year: dateObj.toLocaleString('en-GB', { year: 'numeric', timeZone: agendaDayTimezone }),
          },
          timeGroups: {},
        }
      }

      if (!groups[date].timeGroups[time]) {
        groups[date].timeGroups[time] = {
          time,
          timeLocal,
          sessions: [],
        }
      }

      groups[date].timeGroups[time].sessions.push(session)
    })
    return groups
  }, [sessionsList, post, agendaDayTimezone, isInPerson, timezoneService.timezone])

  const renderTimeHeader = (time, timeLocal) => {
    if (showTimeinSession) return null

    return (
      <div className="hidden sm:block lg:w-1/6 text-2xl font-bold text-gray-900 mb-4">
        <h3 className="list">
          {time}
          <span className="ml-1 text-xs gray-400 font-normal">(Local Time)</span>
        </h3>
        {isInPerson && (
          <p className="font-normal text-sm">
            {timeLocal} <span className="text-xs gray-400">({timezoneService.timezone})</span>
          </p>
        )}
      </div>
    )
  }

  const renderSessions = () => {
    if (Object.keys(groupedSessions).length === 0) {
      return (
        <div className="text-center py-10">
          <h2 className="text-4xl text-gray-900">No results</h2>
          <p className="text-gray-700">Maybe try some different filters?</p>
          <img
            alt="Ghost getting abducted by aliens"
            className="block mx-auto w-1/2 pt-10"
            src={abductionIllustration}
          />
        </div>
      )
    }

    return Object.entries(groupedSessions).map(([date, { dateParts, timeGroups }]) => (
      <div key={date} className="mb-8">
        {/* Main date header */}
        <div className="hidden sm:block w-full bg-white">
          <div className="py-4 px-3 flex justify-between items-center">
            <h2 className="text-4xl font-medium text-gray-600">
              <span>{dateParts.weekday}</span> <span></span>
              <span className="text-gray-600 text-xl">
                {dateParts.day} {dateParts.month} {dateParts.year}
              </span>
            </h2>
          </div>
        </div>

        {/* Time groups */}
        {Object.entries(timeGroups).map(([time, { sessions, timeLocal }]) => (
          <div key={time} className="relative">
            {/* Mobile time header - hidden on desktop */}
            <div className="sm:hidden sticky top-10 -mb-[52px] z-20">
              <div className="py-4 px-3 flex justify-between items-center bg-white">
                <h2 className="text-2xl font-medium text-gray-600 ">
                  <span>{dateParts.weekday}</span> <span></span>
                  <span className="text-gray-600 text-base">
                    {dateParts.day} {dateParts.month} {dateParts.year}
                  </span>
                </h2>
                <div className="font-medium">{time}</div>
              </div>
            </div>

            {/* Sessions for this time */}
            <div className="w-full md:flex px-4 mt-16">
              {renderTimeHeader(time, timeLocal)}
              <div className="w-full">
                {sessions.map((session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    toggleFavourite={toggleFavourite}
                    conferenceLocation={conferenceLocation}
                    conferenceId={session.data.Conference?.[0]?.data?.Conference_Id ?? conferenceId}
                    openFeedbackModal={openFeedbackModal}
                    userUsedPlaces={userUsedPlaces}
                    isAgenda={format === 'conference'}
                    registrations={registrations}
                    showTimeinSession={showTimeinSession}
                    conferenceVenueType={conferenceVenueType}
                    conferenceTimezone={conferenceTimezone}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    ))
  }

  return (
    <div className="max-w-screen-xl mx-auto px-3 sm:px-6" data-cy="session-list">
      {renderSessions()}
    </div>
  )
}

Sessions.propTypes = {
  timezone: PropTypes.string,
  conferenceLocation: PropTypes.string,
  conferenceId: PropTypes.string,
  sessionsList: PropTypes.array,
  toggleFavourite: PropTypes.func.isRequired,
  openFeedbackModal: PropTypes.func.isRequired,
  post: PropTypes.bool,
  format: PropTypes.string.isRequired,
  showTimeinSession: PropTypes.bool,
}

export default withFeedbackModal(withToggleFavouriteSession(Sessions))
